import {pwywCurrencyRowSchema} from "@/features/pricing";
import yup from "../../utils/yup-extended";
import {IS_PWYW_ENABLED} from "../../services/config";

export const MAX_SUBSCRIPTION_TIER_PRICE = 999_999_999;
const TIER_PRICE_TOO_HIGH = "Tier price is too high. Please enter a smaller amount";

/** With the flag off, Pay What You Want is not a Pricing Model a coach can submit. */
const selectableTierPriceTypes = IS_PWYW_ENABLED
  ? ["free", "paid", "pwyw"]
  : ["free", "paid"];

/** A Single Fee row: unchanged from before Pay What You Want existed. */
const singleFeeTierCurrencyRowSchema = yup.object().shape({
  callPriceCurrency: yup.string(),
  callPrice: yup
    .number()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .typeError("Please enter a price for this tier")
    .required("Please enter a price for this tier")
    .positive("Each currency price must be greater than 0 for a paid tier")
    .max(MAX_SUBSCRIPTION_TIER_PRICE, TIER_PRICE_TOO_HIGH),
});

/**
 * Whether an amount is within the tier price limit. An amount not entered yet passes:
 * requiring it is the row schema's job, and reporting both would be two errors at once.
 */
function withinTierPriceLimit(amount: number | undefined) {
  return amount === undefined || !Number.isFinite(amount)
    ? true
    : amount <= MAX_SUBSCRIPTION_TIER_PRICE;
}

/** A Pay What You Want row: the shared row rules, plus the tier price limit on both amounts. */
const pwywTierCurrencyRowSchema = pwywCurrencyRowSchema.shape({
  minCallPrice: pwywCurrencyRowSchema.fields.minCallPrice.test(
    "tier-price-limit",
    TIER_PRICE_TOO_HIGH,
    withinTierPriceLimit
  ),
  callPrice: pwywCurrencyRowSchema.fields.callPrice.test(
    "tier-price-limit",
    TIER_PRICE_TOO_HIGH,
    withinTierPriceLimit
  ),
});

/**
 * A tier's Pricing Model and currency rows, as both subscription schemas validate them.
 *
 * Create and edit are two copies of one schema; these fields are shared so the tier
 * pricing rules cannot drift between them.
 */
export const tierPricingValidationSchemaFields = {
  priceType: yup.string().oneOf(selectableTierPriceTypes).default("paid"),
  currencies: yup.array().when("priceType", (priceType: string, schema) => {
    if (priceType === "free") {
      return schema.notRequired();
    }
    if (IS_PWYW_ENABLED && priceType === "pwyw") {
      return schema.of(pwywTierCurrencyRowSchema);
    }
    return schema.of(singleFeeTierCurrencyRowSchema);
  }),
};
